import { supabase } from '../lib/supabase';

interface FormData {
  formType: 'Contact' | 'Guide' | 'Quote';
  email: string;
  name?: string;
  phone?: string;
  message?: string;
  services?: any;
}

// Submission tracking
const submissionTracker = new Map<string, number>();
const SUBMISSION_COOLDOWN = 60000; // 1 minute cooldown

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_DELAY = 1000; // 1 second

async function wait(delay: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, delay));
}

async function submitWithRetry(insertData: any, retryCount = 0): Promise<any> {
  try {
    const { data, error } = await supabase
      .from('form_submissions')
      .insert([insertData]);

    if (error) {
      if (retryCount < MAX_RETRIES && 
         (error.message.includes('cancelled') || error.message.includes('network'))) {
        const delay = INITIAL_DELAY * Math.pow(2, retryCount);
        console.log(`Retrying submission (attempt ${retryCount + 1} of ${MAX_RETRIES})...`);
        await wait(delay);
        return submitWithRetry(insertData, retryCount + 1);
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      const delay = INITIAL_DELAY * Math.pow(2, retryCount);
      console.log(`Retrying submission (attempt ${retryCount + 1} of ${MAX_RETRIES})...`);
      await wait(delay);
      return submitWithRetry(insertData, retryCount + 1);
    }
    throw error;
  }
}

export async function submitForm(data: FormData) {
  console.log('🚀 FORMS.TS: submitForm called with:', data);
  try {
    if (!data.email) {
      throw new Error('Email is required');
    }

    // Check submission cooldown
    const lastSubmission = submissionTracker.get(data.email);
    const now = Date.now();
    
    if (lastSubmission && now - lastSubmission < SUBMISSION_COOLDOWN) {
      const waitTime = Math.ceil((SUBMISSION_COOLDOWN - (now - lastSubmission)) / 1000);
      throw new Error(`Please wait ${waitTime} seconds before submitting again`);
    }

    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(data.email)) {
      throw new Error('Please enter a valid email address');
    }

    // Combine message and services information
    let finalMessage = data.message || '';
    
    if (data.services) {
      const servicesInfo = typeof data.services === 'string' 
        ? data.services 
        : JSON.stringify(data.services, null, 2);
      
      finalMessage = finalMessage
        ? `${finalMessage}\n\nServices Information:\n${servicesInfo}`
        : `Services Information:\n${servicesInfo}`;
    }

    const insertData = {
      form_type: data.formType,
      email: data.email,
      name: data.name || null,
      phone: data.phone || null,
      message: finalMessage || null
    };

    const { error } = await submitWithRetry(insertData);

    if (error) {
      console.error('Supabase error:', error);
      throw new Error('Failed to submit form. Please try again.');
    }

    // Update submission tracker
    submissionTracker.set(data.email, now);

    return { success: true };
  } catch (error) {
    console.error('Form submission error:', error);
    throw error instanceof Error ? error : new Error('Unable to submit form. Please try again later.');
  }
}